import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '@/lib/axios';
import { Loader2, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { localStorageService } from '@/services/localStorage.service';
import { cookieService } from '@/services/cookie.service';
import { useAuth } from '@/app/providers/AuthProvider';
import { queryClient } from '@/app/providers/QueryProvider';
import { useDocumentMetadata } from '../../../hooks/useDocumentMetadata';
import { authMetadata } from '@/config/metadata';

const RemoteLoginPage = () => {
  useDocumentMetadata(authMetadata.remoteLogin);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const { initializeAuthAsync, setUser } = useAuth();

  useEffect(() => {
    const validateAndLogin = async () => {
      const token = searchParams.get('token');
      const logId = searchParams.get('logId');
      const targetChatbotId = searchParams.get('chatBotId') || '';

      if (!token || !logId) {
        console.error('❌ Missing token or logId:', { token: !!token, logId: !!logId });
        setStatus('error');
        setErrorMessage('Invalid remote access link');
        return;
      }

      console.log('🔐 Preparing remote access session (clearing previous user session if any)...', { logId });

      // Clear any previous user session credentials & state first
      setUser(null);
      await localStorageService.clearAll();
      cookieService.removeCookie('accessToken');
      cookieService.removeCookie('refreshToken');
      queryClient.clear();

      try {
        // Validate remote access token
        console.log('📤 Sending validation request...');
        const response = await axiosInstance.post(
          '/auth/validate-remote-access',
          {
            token,
            logId,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'x-chatbot-id': targetChatbotId,
            },
          }
        );

        console.log('✅ Validation response:', response.data);

        if (response.data.success && response.data.data) {
          const { user: remoteUser } = response.data.data;

          const jwtPayload = token.split('.')[1];
          let tokenTtlDays = 1; // fallback
          try {
            const decoded = JSON.parse(atob(jwtPayload));
            if (decoded?.exp) {
              const msLeft = decoded.exp * 1000 - Date.now();
              tokenTtlDays = Math.max(1 / 1440, msLeft / (1000 * 60 * 60 * 24)); // minimum 1 minute
            }
          } catch {
            // ignore decode errors, keep fallback
          }

          // Set the new remote access token cookie
          cookieService.setCookie('accessToken', token, tokenTtlDays);

          // Store remote access info
          console.log('💾 Storing remote access session...');
          localStorageService.storeUser(remoteUser);
          localStorage.setItem('remoteAccessLogId', logId);
          localStorage.setItem('chatbotId', targetChatbotId || remoteUser.firstChatbotId || '');
          localStorage.setItem('isRemoteAccess', 'true');

          console.log('✅ Remote access session stored successfully');
          setStatus('success');
          toast.success(`Logged in as ${remoteUser.firstName} ${remoteUser.lastName} (Remote Access)`);

          // Redirect to target remote user's dashboard
          setTimeout(async () => {
            const effectiveChatbotId = targetChatbotId || remoteUser.firstChatbotId || '';
            await initializeAuthAsync(effectiveChatbotId);
            navigate('/dashboard', { replace: true });
          }, 1000);
        }
      } catch (error: any) {
        console.error('❌ Remote access validation failed:', error);
        console.error('Error response:', error.response?.data);
        setStatus('error');
        const message =
          error.response?.data?.message ||
          'Failed to validate remote access session';
        setErrorMessage(message);
      }
    };

    validateAndLogin();
  }, [searchParams, navigate]);

  return (
    <div data-testid="remote-login-page" className="min-h-screen flex items-center justify-center bg-linear-to-br from-blue-50 to-indigo-100">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full">
        {status === 'loading' && (
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
            <h2 data-testid="remote-login-title" className="text-2xl font-bold text-gray-800 mb-2">
              Validating Access...
            </h2>
            <p className="text-gray-600">
              Please wait while we verify your remote access session
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 data-testid="remote-login-title" className="text-2xl font-bold text-gray-800 mb-2">
              Access Granted!
            </h2>
            <p className="text-gray-600">Redirecting to dashboard...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <h2 data-testid="remote-login-title" className="text-2xl font-bold text-gray-800 mb-2">
              Access Denied
            </h2>
            <p className="text-red-600 mb-4">{errorMessage}</p>
            <button
              data-testid="remote-login-go-to-login-button"
              onClick={() => navigate('/login')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RemoteLoginPage;
